var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// db/WikiEntry.ts
import { Entity, Field } from "@adornis/baseql/decorators.js";
import { DataLoadedField } from "@adornis/baseql/decorators/data-loaded-field.js";
import { MongoEntity } from "@adornis/baseql/entities/mongoEntity.js";
import { getRawCollection } from "@adornis/baseql/server/collections.js";
import { context } from "@adornis/baseql/server/context.js";
import { loadMongoData } from "@adornis/baseql/server/data-loader.js";
import { CurrentUserInfo } from "@adornis/users/db/currentUserInfo.js";
import { DateTime } from "luxon";
import { deleteWikiEntries, ensureWikiEntries } from "../server/redis.js";
import { WikiBreadcrumb } from "./WikiBreadcrumb.js";
import { logger } from "@adornis/base/logging.js";
var WikiEntry = class extends MongoEntity {
  toString() {
    return this.title ?? "";
  }
  static async beforeCreate(entity) {
    entity.createdAt = DateTime.now();
    entity.modifiedAt = DateTime.now();
    if (!context.serverContext) {
      const caller = await CurrentUserInfo.getMyself()({
        _id: 1
      });
      if (caller) {
        entity.createdByID = caller._id;
        entity.modifiedByID = caller._id;
      }
    }
    if (entity._class === WikiEntry._class) {
      entity.needEmbeddingSync = true;
    } else {
      entity.needEmbeddingSync = false;
    }
    return await super.beforeCreate(entity);
  }
  static async afterCreate(entity, result) {
    await super.afterCreate(entity, result);
    if (entity._id && entity._class === WikiEntry._class) {
      ensureWikiEntries(entity._id).then(() => {
        logger.info(
          {
            _id: entity._id
          },
          "embeddings ensured"
        );
      }).catch((err) => {
        logger.error(err, "could not ensure embeddings");
      });
    }
  }
  static async transformBeforeUpdate(oldEntity, update) {
    update.modifiedAt = DateTime.now();
    if (!context.serverContext) {
      const caller = await CurrentUserInfo.getMyself()({
        _id: 1
      });
      if (caller) {
        update.modifiedByID = caller._id;
      }
    }
    if (update.has("content") && oldEntity.content === update.content || oldEntity._class !== WikiEntry._class) {
      update.needEmbeddingSync = false;
    } else {
      update.needEmbeddingSync = true;
    }
    await super.transformBeforeUpdate(oldEntity, update);
  }
  static async afterUpdate(oldEntity, newEntity, result) {
    await super.afterUpdate(oldEntity, newEntity, result);
    if (newEntity._id) {
      ensureWikiEntries(newEntity._id).then(() => {
        logger.info(
          {
            _id: newEntity._id
          },
          "embeddings ensured"
        );
      }).catch((err) => {
        logger.error(err, "could not ensure embeddings");
      });
    }
  }
  static async afterRemove(id, entityData) {
    await super.afterRemove(id, entityData);
    await deleteWikiEntries(id);
    const wikiCol = await getRawCollection(WikiEntry._collectionName);
    await wikiCol.updateMany({ parentID: id }, { $set: { parentID: null } });
    await wikiCol.deleteMany({ referenceID: id });
  }
};
WikiEntry._class = "WikiEntry";
WikiEntry._collectionName = "wiki-entries";
__decorateClass([
  Field((type) => [WikiBreadcrumb], {
    resolve: function() {
      return async () => {
        const breadcrumbs = [];
        let curId = this.parentID;
        for (let i = 0; i < 5; i++) {
          if (!curId || breadcrumbs.find((b) => b.entryID === curId)) {
            return breadcrumbs;
          }
          const parent = await loadMongoData(WikiEntry, curId);
          if (!parent || !parent.published) {
            return breadcrumbs;
          }
          breadcrumbs.unshift(
            new WikiBreadcrumb({
              entryID: parent._id,
              title: parent.title
            })
          );
          curId = parent.parentID;
        }
        return breadcrumbs;
      };
    }
  })
], WikiEntry.prototype, "breadcrumbs", 2);
__decorateClass([
  Field((type) => String)
], WikiEntry.prototype, "parentID", 2);
__decorateClass([
  DataLoadedField((type) => WikiEntry, ({ parentID }) => parentID)
], WikiEntry.prototype, "parent", 2);
__decorateClass([
  DataLoadedField((type) => [WikiEntry], ({ _id }) => ({ parentID: _id, _class: WikiEntry._class, published: true }))
], WikiEntry.prototype, "children", 2);
__decorateClass([
  Field((type) => String)
], WikiEntry.prototype, "title", 2);
__decorateClass([
  Field((type) => String)
], WikiEntry.prototype, "content", 2);
__decorateClass([
  Field((type) => Boolean)
], WikiEntry.prototype, "published", 2);
__decorateClass([
  Field((type) => Boolean)
], WikiEntry.prototype, "needEmbeddingSync", 2);
__decorateClass([
  Field((type) => DateTime)
], WikiEntry.prototype, "createdAt", 2);
__decorateClass([
  Field((type) => String)
], WikiEntry.prototype, "createdByID", 2);
__decorateClass([
  Field((type) => DateTime)
], WikiEntry.prototype, "modifiedAt", 2);
__decorateClass([
  Field((type) => String)
], WikiEntry.prototype, "modifiedByID", 2);
__decorateClass([
  Field((type) => String)
], WikiEntry.prototype, "readPermission", 2);
__decorateClass([
  Field((type) => String)
], WikiEntry.prototype, "writePermission", 2);
WikiEntry = __decorateClass([
  Entity()
], WikiEntry);
var WikiEntryTemplate = class extends WikiEntry {
  static async ensureDefault() {
    const res = await loadMongoData(WikiEntryTemplate, {
      _class: WikiEntryTemplate._class,
      _id: WikiEntryTemplate.DEFAULT_TEMPLATE_ID
    });
    if (res.length === 0) {
      await new WikiEntryTemplate({
        _class: WikiEntryTemplate._class,
        _id: WikiEntryTemplate.DEFAULT_TEMPLATE_ID,
        title: "Default Template",
        content: "",
        published: true,
        needEmbeddingSync: false
      }).create();
    }
  }
};
WikiEntryTemplate._class = "WikiEntryTemplate";
WikiEntryTemplate.DEFAULT_TEMPLATE_ID = "default-template";
WikiEntryTemplate = __decorateClass([
  Entity()
], WikiEntryTemplate);
export {
  WikiEntry,
  WikiEntryTemplate
};
